import { InventoryAgreement } from 'src/app/models/inventory-agreement.interface';

export type InventoryAgreementKind =
  | 'leave'
  | 'credit-note'
  | 'gas-company'
  | 'client'
  | 'unknown';

export type InventoryPaymentDocumentStatus = 'pending' | 'incomplete' | 'complete';

export interface InventoryPaymentProgress {
  paidAmount: number | null;
  pendingAmount: number | null;
  documentStatus: InventoryPaymentDocumentStatus;
}

export const INVENTORY_AGREEMENT_HELP: Record<InventoryAgreementKind, string> = {
  leave: 'Esta opción se resuelve al terminar la relación comercial con el cliente: se deja en el tanque la misma cantidad de litros con la que se inició el contrato. Si el inventario inicial es cero, esta es la única opción disponible.',
  'credit-note': 'Esta opción se resuelve descontando el monto del inventario inicial en los primeros recibos o facturas que se generen. Úsala si el cliente pidió no recibir más suministro hasta agotar su inventario, pues se podría tener que debitar cualquier remanente.',
  'gas-company': 'Esta opción se resuelve de manera inmediata: la gasera externa emite una factura por el monto del inventario inicial.',
  client: 'Esta opción se resuelve de manera inmediata: el cliente emite una factura por el monto del inventario inicial.',
  unknown: ''
};

export const INVENTORY_AGREEMENT_LABEL: Record<Exclude<InventoryAgreementKind, 'unknown'>, string> = {
  leave: 'Dejar inventario',
  'credit-note': 'Debitar en nota de crédito',
  'gas-company': 'Pagar inventario a gasera',
  client: 'Pagar inventario a cliente'
};

export function calculateInitialInventoryLiters(capacity: unknown, percentage: unknown): number | null {
  if (capacity === null || capacity === undefined || capacity === '' ||
      percentage === null || percentage === undefined || percentage === '') {
    return null;
  }

  const numericCapacity = Number(capacity);
  const numericPercentage = Number(percentage);
  if (!Number.isFinite(numericCapacity) || numericCapacity < 0 ||
      !Number.isFinite(numericPercentage) || numericPercentage < 0 || numericPercentage > 100) {
    return null;
  }

  return Math.round(numericCapacity * numericPercentage) / 100;
}

export function calculateInventoryAmount(liters: unknown, price: unknown): number | null {
  const numericLiters = toNonNegativeNumber(liters);
  const numericPrice = toNonNegativeNumber(price);
  if (numericLiters === null || numericPrice === null) {
    return null;
  }

  return Math.round(numericLiters * numericPrice * 100) / 100;
}

export function calculateRemainingInventoryAmount(total: unknown, applied: unknown): number | null {
  const numericTotal = toNonNegativeNumber(total);
  const numericApplied = toNonNegativeNumber(applied);
  if (numericTotal === null || numericApplied === null) {
    return null;
  }

  return Math.max(Math.round((numericTotal - numericApplied) * 100) / 100, 0);
}

export function calculateInventoryPaymentProgress(
  total: unknown,
  hasPdf: boolean,
  hasXml: boolean
): InventoryPaymentProgress {
  const numericTotal = toNonNegativeNumber(total);
  const hasCompleteInvoice = hasPdf && hasXml;

  return {
    paidAmount: numericTotal === null ? null : hasCompleteInvoice ? numericTotal : 0,
    pendingAmount: numericTotal === null ? null : hasCompleteInvoice ? 0 : numericTotal,
    documentStatus: hasCompleteInvoice ? 'complete' : hasPdf || hasXml ? 'incomplete' : 'pending'
  };
}

export function classifyInventoryAgreement(agreement?: InventoryAgreement | null): InventoryAgreementKind {
  if (!agreement) {
    return 'unknown';
  }

  const text = normalizeAgreementText(`${agreement.name ?? ''} ${agreement.description ?? ''}`);
  if (text.includes('nota') && text.includes('credito')) {
    return 'credit-note';
  }
  if (text.includes('gasera')) {
    return 'gas-company';
  }
  if (text.includes('cliente') && (text.includes('pagar') || text.includes('inventario'))) {
    return 'client';
  }
  if (text.includes('dejar') && text.includes('inventario')) {
    return 'leave';
  }

  return 'unknown';
}

export function findLeaveInventoryAgreement(agreements: InventoryAgreement[]): InventoryAgreement | undefined {
  return agreements.find(agreement => classifyInventoryAgreement(agreement) === 'leave');
}

export function getInventoryAgreementLabel(agreement?: InventoryAgreement | null): string {
  const kind = classifyInventoryAgreement(agreement);
  return kind === 'unknown'
    ? agreement?.description ?? 'Selecciona un acuerdo'
    : INVENTORY_AGREEMENT_LABEL[kind];
}

export function getInventoryAgreementHelp(agreement?: InventoryAgreement | null): string {
  const backendHelp = agreement?.long_description?.trim();
  return backendHelp || INVENTORY_AGREEMENT_HELP[classifyInventoryAgreement(agreement)];
}

function toNonNegativeNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : null;
}

function normalizeAgreementText(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}
